import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { routerActions } from "react-router-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { CommitteesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as CommitteeActions from "../../../actions/committees";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./CommitteesPage.scss";

interface ICommitteesPageParams {}

interface ICommitteesPageProps extends IRouterProps<ICommitteesPageParams> {
    // From state
    committees: Dtos.Committee[];
    formFilter: IFormFilter;
    formLength: number;

    loadingCommittees: boolean;
    loadCommitteesSuccess: boolean;
    loadCommitteesFailure: boolean;
    loadCommitteesErrorCode: string;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
}

interface ICommitteesPageActions extends IRouterActions {
    clearCommittees: CommitteeActions.ICommitteeClearActionCreator;
    loadCommittees: CommitteeActions.ICommitteeLoadActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    filter: CommitteeActions.ICommitteeFilterActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type CommitteesPageProps = ICommitteesPageProps & ICommitteesPageActions;

class CommitteesPage extends React.PureComponent<CommitteesPageProps, any> {
    initialised: boolean;

    constructor(props: CommitteesPageProps) {
        super(props);
        this.initialised = false;
        this.clearCommittees = this.clearCommittees.bind(this);
    }

    componentDidMount() {
        const { 
            loadCommittees, 
            setDrawerItems, 
            setBreadcrumbs
        } = this.props;
        

        setDrawerItems([]);
        setBreadcrumbs(CommitteesBreadcrumbs());

        loadCommittees();
    }

    componentWillReceiveProps(_nextProps: CommitteesPageProps) {}

    componentDidUpdate(_prevProps: CommitteesPageProps, _prevState) {
        const {
            setDrawerItems,
            setBreadcrumbs,
            loadCommitteesFailure,
            navigate,
            loadCommitteesErrorCode,
        } = this.props;

        if (loadCommitteesFailure) {
            navigate(`/error/${loadCommitteesErrorCode}`);
        }

        if (!this.initialised) {
            setDrawerItems([]);
            setBreadcrumbs(CommitteesBreadcrumbs());
            this.initialised = true;
        }
    }

    componentWillUpdate(_nextProps: CommitteesPageProps) {}

    componentWillUnmount() {
        this.clearCommittees();
    }

    clearCommittees() {
        this.props.clearCommittees();
    }

    showCommittees(): boolean {
        const { loadingCommittees, loadCommitteesSuccess } = this.props;

        if (!loadingCommittees && loadCommitteesSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const { loadingCommittees } = this.props;

        return (
            <RestrictedLayout
                loading={loadingCommittees}
                permission={Dtos.Permission.CommitteeView}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const { permissions } = this.props;

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Committees</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={this.props.formFilter}
                            onFilter={(e) => this.props.filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CommitteeCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/committees/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!this.showCommittees() ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {this.showCommittees() ? (
                    <div className="card">
                        <div className="patient-tables">
                            {this.createTable()}
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    createTable() {
        const { committees, pageSize, setPageSize } = this.props;

        if (!committees || committees.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Duotone.COMMITTEE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no committies or committees to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = committees.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={committees}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : committees.length
                    }
                    pageSize={showPagination ? pageSize : committees.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no committees to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string =
                                    "/committees/" +
                                    rowInfo.original.id +
                                    "/details";
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "rank",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.COMMITTEE}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Committee Name",
                            accessor: "name",
                        },
                        {
                            id: "rank",
                            Header: " ",
                            accessor: "rank",
                            maxWidth: 0,
                            Cell: (_props) => <></>,
                        },
                    ]}
                />
            </div>
        );
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: CommitteesPageProps
): ICommitteesPageProps => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        match: ownProps.match,
        history: ownProps.history,
        location: ownProps.location,

        committees: state.committees.formFilteredData,
        formFilter: state.committees.formFilter,
        formLength: state.committees.formLength,

        loadingCommittees:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Pending,
        loadCommitteesSuccess:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Success,
        loadCommitteesFailure:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Failure,
        loadCommitteesErrorCode: state.committees.loadState.errorCode,

        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: CommitteesPageProps
): ICommitteesPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadCommittees: bindActionCreators(
            CommitteeActions.LoadCommittee,
            dispatch
        ),
        clearCommittees: bindActionCreators(CommitteeActions.Clear, dispatch),

        filter: bindActionCreators(CommitteeActions.Filter, dispatch),

        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<ICommitteesPageParams, CommitteesPageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(CommitteesPage) as unknown as ComponentType<CommitteesPageProps>
) as ComponentType<any>;
